import React, { useState, useEffect } from 'react';
import { User as UserType } from '@supabase/supabase-js';
import {
  User,
  Settings,
  Shield,
  LogOut,
  Lock,
  Mail,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
  Gift,
  Users,
  Clock,
  ChevronRight,
  Copy,
  Save,
  MessageCircle,
  TrendingUp,
  TrendingDown,
  Award,
  Globe,
  Phone
} from 'lucide-react';
import TwoFactorSetup from './TwoFactorSetup';
import KycDocumentUpload from './KycDocumentUpload';
import SupportChat from './SupportChat';
import LanguageSwitcher from './LanguageSwitcher';
import PhoneInput from './PhoneInput';
import ReferralTab from './ReferralTab';
import GiveawaySection from './GiveawaySection';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabaseClient';
import { useTranslation } from 'react-i18next';
import { getUserCfdTier } from '../constants/tradingTiers';
import { UserStatus } from '../App';
import { useMarketData } from '../contexts/MarketDataContext';
import {
  DisplayCurrency,
  convertUsdValue,
  getDisplayCurrencySymbol
} from '../utils/displayCurrency';

interface ProfilePageProps {
  user: UserType;
  usdtBalance?: number;
  btcBalance?: number;
  currentPrice?: number;
  onSignOut: () => void;
  onUpdatePassword: (password: string) => Promise<any>;
  referralCode: string | null;
  referralCount: number;
  referredUsers: any[];
  totalPortfolioValue: number;
  totalPositionsPnl?: number;
  userStatus: UserStatus;
  kycStatus: 'not_verified' | 'pending' | 'verified';
  updateKycStatus: (status: 'not_verified' | 'pending' | 'verified') => void;
  portfolioSnapshots?: any[];
  createPortfolioSnapshot?: () => Promise<void>;
  selectedDisplayCurrency: DisplayCurrency;
}

const ProfilePage: React.FC<ProfilePageProps> = ({
  user,
  usdtBalance,
  btcBalance,
  currentPrice,
  onSignOut,
  onUpdatePassword,
  referralCode,
  referralCount,
  referredUsers,
  totalPortfolioValue,
  totalPositionsPnl = 0,
  userStatus,
  kycStatus: propKycStatus,
  updateKycStatus: propUpdateKycStatus,
  portfolioSnapshots,
  createPortfolioSnapshot,
  selectedDisplayCurrency
}) => {
  const { t } = useTranslation();
  const { getPriceBySymbol } = useMarketData();
  const { twoFactorEnabled, disableTwoFactor } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'referrals' | 'giveaway' | 'support'>('profile');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [disabling2FA, setDisabling2FA] = useState(false);
  const [showKycUpload, setShowKycUpload] = useState(false);
  const [kycStatus, setKycStatus] = useState<'not_verified' | 'pending' | 'verified'>(propKycStatus || 'not_verified');

  // Profile information state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const userCfdTier = getUserCfdTier(totalPortfolioValue);
  const selectedCurrencySymbol = getDisplayCurrencySymbol(selectedDisplayCurrency);
  const convertedPortfolioValue = convertUsdValue(
    totalPortfolioValue,
    selectedDisplayCurrency,
    getPriceBySymbol
  );
  const convertedPositionsPnl = convertUsdValue(
    totalPositionsPnl,
    selectedDisplayCurrency,
    getPriceBySymbol
  );

  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina',
    'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
    'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
    'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde',
    'Cambodia', 'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China',
    'Colombia', 'Comoros', 'Congo (Brazzaville)', 'Congo (Kinshasa)', 'Costa Rica', 'Croatia',
    'Cuba', 'Cyprus', 'Czechia', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
    'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini',
    'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
    'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras',
    'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
    'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait',
    'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein',
    'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
    'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco',
    'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
    'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia',
    'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay',
    'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa',
    'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
    'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia',
    'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname',
    'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste',
    'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda',
    'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
    'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ];

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('first_name, last_name, name, country, phone, kyc_status')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching user profile:', error);
          return;
        }

        if (data) {
          let fn = data.first_name || '';
          let ln = data.last_name || '';

          if (!fn && !ln && data.name) {
            const parts = data.name.trim().split(/\s+/);
            fn = parts[0] || '';
            ln = parts.slice(1).join(' ') || '';
          }

          setFirstName(fn);
          setLastName(ln);
          setCountry(data.country || '');

          if (data.phone) {
            const match = data.phone.match(/^(\+\d+)\s*(.*)$/);
            if (match) {
              setCountryCode(match[1]);
              setPhoneNumber(match[2]);
            } else {
              setPhoneNumber(data.phone);
            }
          }

          setKycStatus(data.kyc_status || 'not_verified');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    
    fetchUserProfile();
  }, [user.id]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const { error } = await onUpdatePassword(newPassword);
      
      if (error) {
        throw error;
      }
      
      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisableTwoFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!twoFactorCode) {
      setError('Please enter your two-factor authentication code');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const result = await disableTwoFactor(twoFactorCode);
      
      if (result.error) {
        throw result.error;
      }
      
      setSuccess('Two-factor authentication disabled successfully');
      setTwoFactorCode('');
      setDisabling2FA(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      setSuccess('Referral code copied to clipboard');
      
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setError(null);
    setSuccess(null);
    
    try {
      const fullPhoneNumber = phoneNumber ? `${countryCode} ${phoneNumber}` : '';

      const { error } = await supabase
        .from('users')
        .update({
          first_name: firstName,
          last_name: lastName,
          name: `${firstName} ${lastName}`.trim(),
          country: country,
          phone: fullPhoneNumber
        })
        .eq('id', user.id);
      
      if (error) {
        throw error;
      }
      
      setSuccess('Profile information saved successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to save profile information');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleStartVerification = () => {
    setShowKycUpload(true);
  };

  const handleKycStatusChange = (status: 'not_verified' | 'pending' | 'verified') => {
    setKycStatus(status);
  };

  return (
    <div className="container mx-auto p-8" style={{ background: 'linear-gradient(to bottom, #f8fafc 0%, #ffffff 100%)' }}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div className="flex items-center gap-4">
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)'
            }}
          >
            <User size={32} className="text-white" />
          </div>
          <div>
            <h1
              className="text-3xl font-bold"
              style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {t('profile.accountSettings')}
            </h1>
            <p style={{ color: '#64748b' }}>{user.email}</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div
            className="px-6 py-3 rounded-xl shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
              border: '1px solid rgba(0, 0, 0, 0.08)'
            }}
          >
            <div style={{ color: '#64748b', fontSize: '0.875rem' }}>Portfolio Value</div>
            <div className="font-mono text-xl" style={{ color: '#1e293b' }}>
              {selectedCurrencySymbol}{convertedPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            {totalPositionsPnl !== 0 && (
              <div className="flex items-center gap-1 mt-1">
                {totalPositionsPnl >= 0 ? (
                  <TrendingUp size={14} style={{ color: '#10b981' }} />
                ) : (
                  <TrendingDown size={14} style={{ color: '#ef4444' }} />
                )}
                <span
                  className="text-sm font-medium"
                  style={{ color: totalPositionsPnl >= 0 ? '#10b981' : '#ef4444' }}
                >
                  {convertedPositionsPnl >= 0 ? '+' : ''}{convertedPositionsPnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {selectedDisplayCurrency}
                </span>
              </div>
            )}
          </div>

          <button
            onClick={onSignOut}
            className="px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 text-white"
            style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <LogOut size={18} />
            {t('navigation.signOut')}
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex mb-8" style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
        <button
          onClick={() => setActiveTab('profile')}
          className="px-3 py-2 md:px-6 md:py-3 text-xs md:text-sm font-medium transition-colors"
          style={{
            color: activeTab === 'profile' ? '#3b82f6' : '#64748b',
            borderBottom: activeTab === 'profile' ? '2px solid #3b82f6' : '2px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'profile') e.currentTarget.style.color = '#1e293b';
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'profile') e.currentTarget.style.color = '#64748b';
          }}
        >
          <div className="flex items-center gap-2">
            <User size={18} />
            {t('profile.profileInformation')}
          </div>
        </button>

        <button
          onClick={() => setActiveTab('security')}
          className="px-3 py-2 md:px-6 md:py-3 text-xs md:text-sm font-medium transition-colors"
          style={{
            color: activeTab === 'security' ? '#3b82f6' : '#64748b',
            borderBottom: activeTab === 'security' ? '2px solid #3b82f6' : '2px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'security') e.currentTarget.style.color = '#1e293b';
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'security') e.currentTarget.style.color = '#64748b';
          }}
        >
          <div className="flex items-center gap-2">
            <Shield size={18} />
            {t('profile.security')}
          </div>
        </button>

        <button
          onClick={() => setActiveTab('referrals')}
          className="px-3 py-2 md:px-6 md:py-3 text-xs md:text-sm font-medium transition-colors"
          style={{
            color: activeTab === 'referrals' ? '#3b82f6' : '#64748b',
            borderBottom: activeTab === 'referrals' ? '2px solid #3b82f6' : '2px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'referrals') e.currentTarget.style.color = '#1e293b';
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'referrals') e.currentTarget.style.color = '#64748b';
          }}
        >
          <div className="flex items-center gap-2">
            <Gift size={18} />
            {t('profile.referrals')}
          </div>
        </button>

        <button
          onClick={() => setActiveTab('giveaway')}
          className="px-3 py-2 md:px-6 md:py-3 text-xs md:text-sm font-medium transition-colors"
          style={{
            color: activeTab === 'giveaway' ? '#3b82f6' : '#64748b',
            borderBottom: activeTab === 'giveaway' ? '2px solid #3b82f6' : '2px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'giveaway') e.currentTarget.style.color = '#1e293b';
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'giveaway') e.currentTarget.style.color = '#64748b';
          }}
        >
          <div className="flex items-center gap-2">
            <Gift size={18} />
            Giveaway
          </div>
        </button>

        <button
          onClick={() => setActiveTab('support')}
          className="px-3 py-2 md:px-6 md:py-3 text-xs md:text-sm font-medium transition-colors"
          style={{
            color: activeTab === 'support' ? '#3b82f6' : '#64748b',
            borderBottom: activeTab === 'support' ? '2px solid #3b82f6' : '2px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (activeTab !== 'support') e.currentTarget.style.color = '#1e293b';
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'support') e.currentTarget.style.color = '#64748b';
          }}
        >
          <div className="flex items-center gap-2">
            <MessageCircle size={18} />
            {t('profile.support')}
          </div>
        </button>
      </div>
      
      {/* Status Messages */}
      {error && (
        <div className="rounded-xl p-4 flex items-center gap-3 mb-6" style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)'
        }}>
          <AlertTriangle size={20} style={{ color: '#ef4444' }} className="flex-shrink-0" />
          <span style={{ color: '#dc2626' }}>{error}</span>
        </div>
      )}

      {success && (
        <div className="rounded-xl p-4 flex items-center gap-3 mb-6" style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.3)'
        }}>
          <CheckCircle size={20} style={{ color: '#10b981' }} className="flex-shrink-0" />
          <span style={{ color: '#059669' }}>{success}</span>
        </div>
      )}
      
      {/* Tab Content */}
      {activeTab === 'profile' && (
        <div>
          {/* Top Row - Account Summary and Balances Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Account Summary */}
            <div
              className="rounded-2xl p-6 shadow-2xl"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#1e293b' }}>{t('profile.accountSummary')}</h3>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span style={{ color: '#64748b' }}>{t('profile.accountType')}</span>
                  <span className="font-medium" style={{ color: '#1e293b' }}>{t('profile.standardAccountType')}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span style={{ color: '#64748b' }}>{t('profile.memberSince')}</span>
                  <span className="font-medium" style={{ color: '#1e293b' }}>{new Date(user.created_at || Date.now()).toLocaleDateString()}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span style={{ color: '#64748b' }}>{t('profile.kycStatus')}</span>
                  <span
                    className="px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      background: kycStatus === 'verified'
                        ? 'rgba(16, 185, 129, 0.15)'
                        : 'rgba(245, 158, 11, 0.15)',
                      color: kycStatus === 'verified' ? '#10b981' : '#f59e0b'
                    }}
                  >
                    {kycStatus === 'verified' ? 'Verified' :
                     kycStatus === 'pending' ? 'Pending' :
                     t('profile.notVerified')}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span style={{ color: '#64748b' }}>{t('profile.twoFactorStatus')}</span>
                  <span
                    className="px-2 py-1 rounded-full text-xs font-medium"
                    style={{
                      background: twoFactorEnabled
                        ? 'rgba(16, 185, 129, 0.15)'
                        : 'rgba(239, 68, 68, 0.15)',
                      color: twoFactorEnabled ? '#10b981' : '#ef4444'
                    }}
                  >
                    {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div className="mt-6 pt-6" style={{ borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <button
                  onClick={() => {
                    const element = document.getElementById('profile-info-section');
                    element?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full text-white py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {t('profile.completeProfile')}
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            {/* Account Balances */}
            <div
              className="rounded-2xl p-6 shadow-2xl"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              <h3 className="text-lg font-semibold mb-4" style={{ color: '#1e293b' }}>{t('profile.accountBalances')}</h3>

              <div className="space-y-4">
                <div
                  className="rounded-lg p-4"
                  style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(16, 185, 129, 0.15)' }}
                      >
                        <span className="font-bold" style={{ color: '#10b981' }}>$</span>
                      </div>
                      <span className="font-medium" style={{ color: '#1e293b' }}>USDT</span>
                    </div>
                    <span className="font-mono" style={{ color: '#1e293b' }}>{(usdtBalance || 0).toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-right" style={{ color: '#64748b' }}>
                    ${(usdtBalance || 0).toFixed(2)} USD
                  </div>
                </div>

                <div
                  className="rounded-lg p-4"
                  style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(249, 115, 22, 0.15)' }}
                      >
                        <span className="font-bold" style={{ color: '#f97316' }}>₿</span>
                      </div>
                      <span className="font-medium" style={{ color: '#1e293b' }}>BTC</span>
                    </div>
                    <span className="font-mono" style={{ color: '#1e293b' }}>{(btcBalance || 0).toFixed(8)}</span>
                  </div>
                  <div className="text-xs text-right" style={{ color: '#64748b' }}>
                    ${((btcBalance || 0) * (currentPrice || 0)).toFixed(2)} USD
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Information - Full Width */}
          <div id="profile-info-section">
            {/* Profile Tab */}
            <div
              className="rounded-2xl p-8 shadow-2xl relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              {/* Decorative gradient accent */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)'
                }}
              />
              <h2 className="text-xl font-semibold mb-6 flex items-center gap-3" style={{ color: '#1e293b' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <User size={20} className="text-white" />
                </div>
                {t('profile.profileInformation')}
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm mb-2" style={{ color: '#64748b' }}>{t('auth.email')}</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: '#94a3b8' }} />
                    <input
                      type="email"
                      value={user.email || ''}
                      readOnly
                      className="w-full pl-10 pr-4 py-3 rounded-xl focus:outline-none"
                      style={{
                        background: 'rgba(0, 0, 0, 0.02)',
                        color: '#1e293b',
                        border: '1px solid rgba(0, 0, 0, 0.08)'
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-2" style={{ color: '#64748b' }}>{t('auth.firstName')}</label>
                    <input
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500/50 transition-all"
                      style={{
                        background: 'rgba(0, 0, 0, 0.02)',
                        color: '#1e293b',
                        border: '1px solid rgba(0, 0, 0, 0.08)'
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm mb-2" style={{ color: '#64748b' }}>{t('auth.lastName')}</label>
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500/50 transition-all"
                      style={{
                        background: 'rgba(0, 0, 0, 0.02)',
                        color: '#1e293b',
                        border: '1px solid rgba(0, 0, 0, 0.08)'
                      }}
                    />
                  </div>
                </div>
                
                <PhoneInput
                  value={phoneNumber}
                  countryCode={countryCode}
                  onCountryCodeChange={setCountryCode}
                  onPhoneNumberChange={setPhoneNumber}
                />

                <div>
                  <label className="block text-sm mb-2" style={{ color: '#64748b' }}>{t('auth.country')}</label>
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all custom-select"
                    style={{
                      background: 'rgba(0, 0, 0, 0.02)',
                      color: '#1e293b',
                      border: '1px solid rgba(0, 0, 0, 0.08)'
                    }}
                  >
                    <option value="">{t('profile.selectCountry')}</option>
                    {countries.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                {/* Language Preferences */}
                <div
                  className="rounded-xl p-6"
                  style={{
                    background: '#ffffff',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#1e293b' }}>
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
                      }}
                    >
                      <Globe size={18} className="text-white" />
                    </div>
                    Language Preferences
                  </h3>
                  <p className="mb-4 text-sm" style={{ color: '#64748b' }}>
                    Select your preferred language for the platform interface
                  </p>
                  <LanguageSwitcher />
                </div>

                {/* Portfolio Status Tiers */}
                <div
                  className="rounded-2xl p-6 shadow-2xl relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
                    border: '1px solid rgba(59, 130, 246, 0.2)'
                  }}
                >
                  {/* Top gradient accent */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #10b981 0%, #3b82f6 50%, #8b5cf6 100%)'
                    }}
                  />
                  <div className="text-center mb-6">
                    <h3
                      className="text-2xl font-bold mb-2"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 50%, #8b5cf6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      Portfolio Status Tiers
                    </h3>
                    <p className="text-sm" style={{ color: '#64748b' }}>
                      Your status is determined by your total portfolio value
                    </p>
                  </div>

                  {/* Current Status Badge */}
                  <div
                    className="rounded-xl p-4 mb-6 text-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.3)'
                    }}
                  >
                    <div className="text-sm mb-1" style={{ color: '#64748b' }}>Your Current Status</div>
                    <div className="flex items-center justify-center gap-2">
                      <Award size={24} style={{ color: '#10b981' }} />
                      <span
                        className="text-3xl font-bold"
                        style={{
                          background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}
                      >
                        {userStatus}
                      </span>
                    </div>
                    <div className="text-lg mt-2 font-semibold" style={{ color: '#1e293b' }}>
                      ${totalPortfolioValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>

                  {/* Status Tiers Grid */}
                  <div className="space-y-3">
                    {[
                      { name: 'No-Coiner', threshold: '$0 - $249', color: 'from-slate-500 to-slate-600', icon: '🪙', emoji: '💸' },
                      { name: 'Shrimp', threshold: '$250+', color: 'from-red-500 to-orange-500', icon: '🦐', emoji: '🦐' },
                      { name: 'Crab', threshold: '$10K+', color: 'from-orange-500 to-yellow-500', icon: '🦀', emoji: '🦀' },
                      { name: 'Octopus', threshold: '$25K+', color: 'from-yellow-500 to-green-500', icon: '🐙', emoji: '🐙' },
                      { name: 'Dolphin', threshold: '$50K+', color: 'from-green-500 to-cyan-500', icon: '🐬', emoji: '🐬' },
                      { name: 'Shark', threshold: '$100K+', color: 'from-cyan-500 to-blue-500', icon: '🦈', emoji: '🦈' },
                      { name: 'Whale', threshold: '$500K+', color: 'from-blue-500 to-purple-500', icon: '🐋', emoji: '🐋' },
                      { name: 'Humpback', threshold: '$2M+', color: 'from-purple-500 to-pink-500', icon: '🐳', emoji: '🐳' },
                    ].map((tier) => (
                      <div
                        key={tier.name}
                        className="relative rounded-xl p-4 border transition-all duration-300"
                        style={{
                          background: tier.name === userStatus
                            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(59, 130, 246, 0.08) 100%)'
                            : 'rgba(0, 0, 0, 0.02)',
                          border: tier.name === userStatus
                            ? '2px solid rgba(16, 185, 129, 0.4)'
                            : '1px solid rgba(0, 0, 0, 0.08)',
                          boxShadow: tier.name === userStatus
                            ? '0 4px 12px rgba(16, 185, 129, 0.2)'
                            : 'none'
                        }}
                      >
                        {tier.name === userStatus && (
                          <div
                            className="absolute -top-2 -right-2 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg"
                            style={{ background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)' }}
                          >
                            YOU
                          </div>
                        )}
                        <div className="flex items-center gap-4">
                          <div className="text-4xl">{tier.emoji}</div>
                          <div className="flex-1">
                            <div
                              className="font-bold text-lg text-black"
                            >
                              {tier.name}
                            </div>
                            <div className="text-sm" style={{ color: '#64748b' }}>{tier.threshold} Portfolio Value</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Progress Info */}
                  <div
                    className="mt-6 rounded-xl p-4"
                    style={{
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
                      border: '1px solid rgba(59, 130, 246, 0.2)'
                    }}
                  >
                    <p className="text-sm text-center" style={{ color: '#475569' }}>
                      Keep growing your portfolio to reach higher status tiers and unlock exclusive benefits
                    </p>
                  </div>
                </div>

                {/* CFD Trading Tiers */}
                <div
                  className="rounded-2xl p-6 shadow-2xl relative overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)',
                    border: '1px solid rgba(139, 92, 246, 0.2)'
                  }}
                >
                  {/* Top gradient accent */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #8b5cf6 0%, #ec4899 50%, #3b82f6 100%)'
                    }}
                  />
                  <div className="text-center mb-6">
                    <h3
                      className="text-2xl font-bold mb-2"
                      style={{
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%, #3b82f6 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      CFD Trading Tiers
                    </h3>
                    <p className="text-sm" style={{ color: '#64748b' }}>
                      Grow your portfolio to unlock higher leverage limits and better trading conditions
                    </p>
                  </div>

                  {/* Current Tier Badge */}
                  <div
                    className="rounded-xl p-4 mb-6 text-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                      border: '1px solid rgba(139, 92, 246, 0.3)'
                    }}
                  >
                    <div className="text-sm mb-1" style={{ color: '#64748b' }}>Your Current Tier</div>
                    <div className="flex items-center justify-center gap-2">
                      <Award size={24} style={{ color: '#f59e0b' }} />
                      <span
                        className="text-3xl font-bold"
                        style={{
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}
                      >
                        {userCfdTier.name}
                      </span>
                    </div>
                    <div className="text-sm mt-2" style={{ color: '#64748b' }}>{userCfdTier.description}</div>
                  </div>

                  {/* Tier Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {[
                      { name: 'Starter', minEquity: '$0', maxForex: 100, maxCommodities: 30, maxStocks: 20, icon: '📊' },
                      { name: 'Plus', minEquity: '$10K', maxForex: 200, maxCommodities: 50, maxStocks: 40, icon: '📈' },
                      { name: 'Advanced', minEquity: '$50K', maxForex: 300, maxCommodities: 75, maxStocks: 60, icon: '💹' },
                      { name: 'Pro', minEquity: '$100K', maxForex: 500, maxCommodities: 90, maxStocks: 80, icon: '🏆' },
                      { name: 'Elite', minEquity: '$250K', maxForex: 1000, maxCommodities: 100, maxStocks: 100, icon: '👑' },
                    ].map((tier) => (
                      <div
                        key={tier.name}
                        className="relative rounded-xl p-4 border transition-all duration-300"
                        style={{
                          background: tier.name === userCfdTier.name
                            ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(236, 72, 153, 0.08) 100%)'
                            : 'rgba(0, 0, 0, 0.02)',
                          border: tier.name === userCfdTier.name
                            ? '2px solid rgba(139, 92, 246, 0.4)'
                            : '1px solid rgba(0, 0, 0, 0.08)',
                          boxShadow: tier.name === userCfdTier.name
                            ? '0 4px 12px rgba(139, 92, 246, 0.2)'
                            : 'none'
                        }}
                      >
                        {tier.name === userCfdTier.name && (
                          <div
                            className="absolute -top-2 -right-2 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg"
                            style={{ background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)' }}
                          >
                            ACTIVE
                          </div>
                        )}
                        <div className="text-center mb-3">
                          <div className="text-4xl mb-2">{tier.icon}</div>
                          <div className="font-bold text-lg" style={{ color: '#1e293b' }}>{tier.name}</div>
                          <div className="text-xs" style={{ color: '#64748b' }}>Min. {tier.minEquity}</div>
                        </div>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between items-center">
                            <span style={{ color: '#64748b' }}>Forex:</span>
                            <span className="font-bold" style={{ color: '#10b981' }}>{tier.maxForex}x</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span style={{ color: '#64748b' }}>Commodities:</span>
                            <span className="font-bold" style={{ color: '#3b82f6' }}>{tier.maxCommodities}x</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span style={{ color: '#64748b' }}>Stocks:</span>
                            <span className="font-bold" style={{ color: '#8b5cf6' }}>{tier.maxStocks}x</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Your Limits */}
                  <div
                    className="rounded-xl p-4"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(59, 130, 246, 0.08) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.2)'
                    }}
                  >
                    <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#1e293b' }}>
                      <TrendingUp size={18} style={{ color: '#10b981' }} />
                      Your Current Maximum Leverage
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold" style={{ color: '#10b981' }}>{userCfdTier.maxForex}x</div>
                        <div className="text-xs mt-1" style={{ color: '#64748b' }}>Forex</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold" style={{ color: '#3b82f6' }}>{userCfdTier.maxCommodities}x</div>
                        <div className="text-xs mt-1" style={{ color: '#64748b' }}>Commodities</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold" style={{ color: '#8b5cf6' }}>{userCfdTier.maxStocks}x</div>
                        <div className="text-xs mt-1" style={{ color: '#64748b' }}>Stocks</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 text-center text-xs" style={{ color: '#94a3b8' }}>
                    💡 Your leverage limits are automatically adjusted based on your total portfolio value
                  </div>
                </div>

                {/* KYC Section */}
                <div
                  id="kyc-section"
                  className="rounded-xl p-6"
                  style={{
                    background: 'rgba(0, 0, 0, 0.02)',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#1e293b' }}>{t('profile.kycVerification')}</h3>
                  <p className="mb-4" style={{ color: '#64748b' }}>
                    {t('profile.kycDescription')}
                  </p>

                  <div className="flex items-center gap-4 mb-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{
                        background: kycStatus === 'verified'
                          ? 'rgba(16, 185, 129, 0.15)'
                          : 'rgba(245, 158, 11, 0.15)'
                      }}
                    >
                      {kycStatus === 'verified' ? (
                        <CheckCircle size={20} style={{ color: '#10b981' }} />
                      ) : kycStatus === 'pending' ? (
                        <Clock size={20} style={{ color: '#f59e0b' }} />
                      ) : (
                        <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
                      )}
                    </div>
                    <div>
                      <div className="font-medium" style={{ color: '#1e293b' }}>
                        {kycStatus === 'verified' ? t('profile.verificationComplete') :
                         kycStatus === 'pending' ? t('profile.verificationPending') :
                         t('profile.verificationRequired')}
                      </div>
                      <div className="text-sm" style={{ color: '#64748b' }}>
                        {kycStatus === 'verified' ? t('profile.accountVerified') :
                         kycStatus === 'pending' ? t('profile.documentsReviewed') :
                         t('profile.accountNotVerified')}
                      </div>
                    </div>
                  </div>
                  
                  {kycStatus === 'not_verified' && (
                    <button
                      onClick={handleStartVerification}
                      className="text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                      style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {t('profile.startVerification')}
                      <ChevronRight size={18} />
                    </button>
                  )}

                  {kycStatus === 'pending' && (
                    <div className="rounded-lg p-4 text-sm" style={{
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      color: '#f59e0b'
                    }}>
                      {t('profile.verificationPending')}
                    </div>
                  )}

                  {kycStatus === 'verified' && (
                    <div className="rounded-lg p-4 text-sm" style={{
                      background: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      color: '#10b981'
                    }}>
                      {t('profile.verificationComplete')}
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                    style={{
                      background: savingProfile
                        ? 'linear-gradient(135deg, #64748b 0%, #475569 100%)'
                        : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                      opacity: savingProfile ? 0.6 : 1,
                      cursor: savingProfile ? 'not-allowed' : 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      if (!savingProfile) {
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!savingProfile) {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {savingProfile ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Save size={18} />
                        {t('profile.saveChanges')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div>
            <div className="space-y-8">
              {/* Change Password */}
              <div
                className="rounded-2xl p-8 shadow-2xl relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid rgba(0, 0, 0, 0.08)'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)'
                  }}
                />
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-3" style={{ color: '#1e293b' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                    }}
                  >
                    <Lock size={20} className="text-white" />
                  </div>
                  {t('profile.changePassword')}
                </h2>
                
                <form onSubmit={handlePasswordChange} className="space-y-6">
                  <div>
                    <label className="block text-sm mb-2" style={{ color: '#64748b' }}>{t('profile.currentPassword')}</label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: '#94a3b8' }} />
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder={t('profile.enterCurrentPassword')}
                        className="w-full pl-10 pr-10 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                        style={{
                          background: 'rgba(0, 0, 0, 0.02)',
                          color: '#1e293b',
                          border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors"
                        style={{ color: '#94a3b8' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#1e293b'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                      >
                        {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm mb-2" style={{ color: '#64748b' }}>{t('profile.newPassword')}</label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: '#94a3b8' }} />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder={t('profile.enterNewPassword')}
                        className="w-full pl-10 pr-10 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                        minLength={6}
                        style={{
                          background: 'rgba(0, 0, 0, 0.02)',
                          color: '#1e293b',
                          border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors"
                        style={{ color: '#94a3b8' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#1e293b'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <p className="text-xs mt-1 ml-1" style={{ color: '#94a3b8' }}>
                      {t('profile.passwordMinLength')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm mb-2" style={{ color: '#64748b' }}>{t('profile.confirmNewPassword')}</label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: '#94a3b8' }} />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className="w-full pl-10 pr-10 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                        minLength={6}
                        style={{
                          background: 'rgba(0, 0, 0, 0.02)',
                          color: '#1e293b',
                          border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors"
                        style={{ color: '#94a3b8' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#1e293b'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading || !newPassword || !confirmPassword}
                      className="text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                      style={{
                        background: (loading || !newPassword || !confirmPassword)
                          ? 'linear-gradient(135deg, #64748b 0%, #475569 100%)'
                          : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                        opacity: (loading || !newPassword || !confirmPassword) ? 0.6 : 1,
                        cursor: (loading || !newPassword || !confirmPassword) ? 'not-allowed' : 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading && newPassword && confirmPassword) {
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading && newPassword && confirmPassword) {
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        t('profile.updatePassword')
                      )}
                    </button>
                  </div>
                </form>
              </div>
              
              {/* Two-Factor Authentication */}
              <div
                className="rounded-2xl p-8 shadow-2xl relative overflow-hidden"
                style={{
                  background: '#ffffff',
                  border: '1px solid rgba(0, 0, 0, 0.08)'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #10b981 0%, #3b82f6 50%, #8b5cf6 100%)'
                  }}
                />
                <h2 className="text-xl font-semibold mb-6 flex items-center gap-3" style={{ color: '#1e293b' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #10b981 0%, #3b82f6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                    }}
                  >
                    <Shield size={20} className="text-white" />
                  </div>
                  {t('profile.twoFactorAuth')}
                </h2>
                
                {showTwoFactorSetup ? (
                  <TwoFactorSetup
                    onComplete={() => {
                      setShowTwoFactorSetup(false);
                      setSuccess('Two-factor authentication enabled successfully');
                      window.location.reload();
                    }}
                    onCancel={() => setShowTwoFactorSetup(false)}
                  />
                ) : disabling2FA ? (
                  <form onSubmit={handleDisableTwoFactor} className="space-y-6">
                    <p className="mb-4" style={{ color: '#475569' }}>
                      {t('profile.enterTwoFactorCodeToDisable')}
                    </p>

                    <div>
                      <label className="block text-sm mb-2" style={{ color: '#64748b' }}>
                        {t('profile.verificationCode')}
                      </label>
                      <input
                        type="text"
                        value={twoFactorCode}
                        onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                        placeholder="Enter 6-digit code"
                        className="w-full px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                        style={{
                          background: 'rgba(0, 0, 0, 0.02)',
                          color: '#1e293b',
                          border: '1px solid rgba(0, 0, 0, 0.08)'
                        }}
                        maxLength={6}
                        pattern="[0-9]{6}"
                        required
                      />
                    </div>

                    <div className="flex justify-between">
                      <button
                        type="button"
                        onClick={() => setDisabling2FA(false)}
                        className="text-white px-6 py-3 rounded-xl font-medium transition-all"
                        style={{
                          background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                          boxShadow: '0 4px 12px rgba(100, 116, 139, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(100, 116, 139, 0.4)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(100, 116, 139, 0.3)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        {t('common.cancel')}
                      </button>

                      <button
                        type="submit"
                        disabled={loading || twoFactorCode.length !== 6}
                        className="text-white px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2"
                        style={{
                          background: (loading || twoFactorCode.length !== 6)
                            ? 'linear-gradient(135deg, #64748b 0%, #475569 100%)'
                            : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                          opacity: (loading || twoFactorCode.length !== 6) ? 0.6 : 1,
                          cursor: (loading || twoFactorCode.length !== 6) ? 'not-allowed' : 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading && twoFactorCode.length === 6) {
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading && twoFactorCode.length === 6) {
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          t('profile.disableTwoFactor')
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <p className="font-medium mb-1" style={{ color: '#1e293b' }}>
                          {twoFactorEnabled ? t('profile.twoFactorEnabledStatus') : t('profile.twoFactorNotEnabledStatus')}
                        </p>
                        <p className="text-sm" style={{ color: '#64748b' }}>
                          {twoFactorEnabled ? t('profile.accountProtectedWithTwoFactor') : t('profile.addExtraSecurityLayer')}
                        </p>
                      </div>

                      <div
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{
                          background: twoFactorEnabled
                            ? 'rgba(16, 185, 129, 0.15)'
                            : 'rgba(245, 158, 11, 0.15)',
                          color: twoFactorEnabled ? '#10b981' : '#f59e0b'
                        }}
                      >
                        {twoFactorEnabled ? t('profile.twoFactorEnabledStatus') : t('profile.twoFactorDisabledStatus')}
                      </div>
                    </div>

                    <p className="text-sm mb-6" style={{ color: '#475569' }}>
                      {t('profile.twoFactorFullDescription')}
                    </p>

                    {twoFactorEnabled ? (
                      <button
                        onClick={() => setDisabling2FA(true)}
                        className="text-white px-6 py-3 rounded-xl font-semibold transition-all"
                        style={{
                          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.4)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        {t('profile.disableTwoFactor')}
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowTwoFactorSetup(true)}
                        className="text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                        style={{
                          background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        {t('profile.enableTwoFactor')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
        </div>
      )}

      {/* Referrals Tab */}
      {activeTab === 'referrals' && (
        <ReferralTab />
      )}

      {/* Giveaway Tab */}
      {activeTab === 'giveaway' && (
        <GiveawaySection />
      )}

      {/* Support Tab */}
      {activeTab === 'support' && (
        <SupportChat user={user} />
      )}

      {/* KYC Document Upload Modal */}
      {showKycUpload && (
        <KycDocumentUpload 
          onClose={() => setShowKycUpload(false)}
          onKycStatusChange={handleKycStatusChange}
        />
      )}
    </div>
  );
};

export default ProfilePage;
